// ============================================================
// /src/hooks/useProWycena.ts (JEDYNY HOOK - ORKIESTRACJA)
// ============================================================

import { useState, useCallback } from "react";
import { Estimate, SpatialData } from "../domain/types";
import { analyzeSpatial, calculateFromReference } from "../domain/vision";
import { calculateCosts } from "../domain/cost";
import { MATERIAL_RATE_M2 } from "@/core/zasady/constants";
import { validateBusinessRules } from "../domain/rules";
import { applyMargin } from "../domain/margin";

type FlowState = 
  | { stage: "IDLE" }
  | { stage: "UPLOADING" }
  | { stage: "ANALYZING"; file: File }
  | { stage: "REFERENCE_SELECT"; spatial: SpatialData }
  | { stage: "CALCULATING" }
  | { stage: "RESULT"; estimate: Estimate }
  | { stage: "ERROR"; message: string };

export function useProWycena() {
  const [flow, setFlow] = useState<FlowState>({ stage: "IDLE" });

  const uploadImage = useCallback(async (file: File) => {
    if (!file) return;
    
    setFlow({ stage: "ANALYZING", file });
    
    try {
      const spatial = await analyzeSpatial(file);
      
      if (spatial.confidence < 50) {
        setFlow({ 
          stage: "REFERENCE_SELECT", 
          spatial: { ...spatial, confidence: 0 } 
        });
      } else {
        calculateEstimate(spatial);
      }
    } catch (err) {
      setFlow({ stage: "ERROR", message: "VISION_FAILURE" });
    }
  }, []);

  const selectReference = useCallback((ref: SpatialData["referenceObject"]) => {
    if (flow.stage !== "REFERENCE_SELECT") return;
    
    // Przeliczenie z nowym punktem odniesienia
    const recalculated = calculateFromReference(
      { width: 800, height: 600 }, // z canvas
      ref
    );
    
    calculateEstimate(recalculated);
  }, [flow]);

  const calculateEstimate = useCallback((spatial: SpatialData) => {
    setFlow({ stage: "CALCULATING" });
    
    // Symulacja BOM - w produkcji z external service
    const materialCosts = spatial.flatSurfaceM2 * MATERIAL_RATE_M2; // mock BOM
    
    let costs = calculateCosts(spatial, materialCosts);
    costs = applyMargin(costs, spatial.confidence);
    
    const estimate: Estimate = {
      id: `est-${Date.now()}`,
      source: spatial.confidence > 80 ? "vision" : "manual",
      spatial,
      costs,
      rules: [], // uzupełni validateBusinessRules
      meta: {
        createdAt: Date.now(),
        updatedAt: Date.now(),
        aiConfidence: spatial.confidence
      }
    };
    
    estimate.rules = validateBusinessRules(estimate);
    
    setFlow({ stage: "RESULT", estimate });
  }, []);

  const reset = useCallback(() => {
    setFlow({ stage: "IDLE" });
  }, []);

  return {
    flow,
    uploadImage,
    selectReference,
    reset
  };
}
